import { useEffect, useReducer, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Link2, ImagePlus, Undo2, Redo2, Loader2, Quote,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

/** Upload an image to S3 via the admin blog presigned-URL endpoint. */
export async function uploadBlogImage(file: File): Promise<string> {
  const { uploadUrl, publicUrl } = await api<{ uploadUrl: string; publicUrl: string }>(
    '/api/admin/blog/image-upload-url',
    { method: 'POST', body: JSON.stringify({ mimeType: file.type, sizeBytes: file.size }) },
  );
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type, 'Cache-Control': 'public, max-age=31536000' },
    body: file,
  });
  if (!put.ok) throw new Error('upload failed');
  return publicUrl;
}

/** A rich text editor (TipTap) producing HTML. Mount fresh per modal open. */
export function RichTextEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: {
          openOnClick: false,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Image.configure({ HTMLAttributes: { class: 'rounded-xl' } }),
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: 'blog-content px-4 py-3' } },
  });

  // TipTap v3 does not re-render the host component on every transaction;
  // force it so the toolbar's active states stay accurate.
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => force();
    editor.on('transaction', handler);
    return () => {
      editor.off('transaction', handler);
    };
  }, [editor]);

  if (!editor) {
    return (
      <div className="border border-slate-200 rounded-xl h-72 flex items-center justify-center text-slate-300">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="tiptap-editor border border-slate-200 rounded-xl overflow-hidden bg-white">
      <Toolbar editor={editor} />
      <div className="max-h-[460px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/* ── Toolbar ───────────────────────────────────────────────── */
function Toolbar({ editor }: { editor: Editor }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  function setLink() {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL del enlace:', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  async function handleImage(file: File) {
    setUploading(true);
    try {
      const url = await uploadBlogImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch {
      window.alert('No se pudo subir la imagen.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleImage(f);
          e.target.value = '';
        }}
      />
      <Btn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrita">
        <Bold size={15} />
      </Btn>
      <Btn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Cursiva">
        <Italic size={15} />
      </Btn>
      <Btn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Subrayado">
        <UnderlineIcon size={15} />
      </Btn>
      <Sep />
      <Btn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Título 1">
        <Heading1 size={15} />
      </Btn>
      <Btn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Título 2">
        <Heading2 size={15} />
      </Btn>
      <Btn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Título 3">
        <Heading3 size={15} />
      </Btn>
      <Sep />
      <Btn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista">
        <List size={15} />
      </Btn>
      <Btn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada">
        <ListOrdered size={15} />
      </Btn>
      <Btn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Cita">
        <Quote size={15} />
      </Btn>
      <Sep />
      <Btn active={editor.isActive('link')} onClick={setLink} title="Enlace">
        <Link2 size={15} />
      </Btn>
      <Btn active={false} onClick={() => fileInput.current?.click()} title="Imagen" disabled={uploading}>
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
      </Btn>
      <Sep />
      <Btn active={false} onClick={() => editor.chain().focus().undo().run()} title="Deshacer" disabled={!editor.can().undo()}>
        <Undo2 size={15} />
      </Btn>
      <Btn active={false} onClick={() => editor.chain().focus().redo().run()} title="Rehacer" disabled={!editor.can().redo()}>
        <Redo2 size={15} />
      </Btn>
    </div>
  );
}

function Btn({
  active,
  onClick,
  title,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'h-8 w-8 inline-flex items-center justify-center rounded-md transition disabled:opacity-30',
        active ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-200',
      )}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="mx-1 h-5 w-px bg-slate-200" />;
}
